import { IconInfoCircle } from "@tabler/icons-react"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "design-system/atoms/Dialog"
import { Label } from "design-system/atoms/Label"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { MarkdownRenderer } from "design-system/organisms/chat/MarkdownRenderer"
import { renderLiquid } from "lopermut"
import { Braces, PencilIcon } from "lucide-react"
import { VaultVariableAutocomplete } from "modules/shared/components/VaultVariableAutocomplete"
import { useVaultPreviewContext } from "modules/shared/hooks/useVaultPreviewContext"
import { useEffect, useMemo, useRef, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { CAMPAIGN_ACTION_WEBHOOK_DEFAULT_PAYLOAD } from "schema"
import { useWorkflowBuilder } from "../../hooks/useWorkflowBuilder"

type Props = {
  initialPayload: string
  onChangePayload: (payload: string) => void
  eventPayload: Record<string, unknown>
  eventName: string
  nodeIndex?: number
}

export function EditWebhookPayloadDialog({
  initialPayload,
  onChangePayload,
  eventPayload,
  eventName,
  nodeIndex,
}: Props) {
  const { t } = useTranslation()
  const { form } = useWorkflowBuilder()
  const [payload, setPayload] = useState(initialPayload)
  const [isOpen, setIsOpen] = useState(false)
  const previewContext = useVaultPreviewContext(eventPayload)

  const renderedPayload = useMemo(() => {
    try {
      const rendered = renderLiquid(
        payload.trim() === "" ? "null" : payload.trim(),
        previewContext,
        { jsonSafe: true },
      )
      if (rendered) {
        JSON.parse(rendered)
        return rendered
      }
    } catch {
      return null
    }
  }, [previewContext, payload])

  const isValidPayload = !!renderedPayload

  useEffect(() => {
    if (isOpen) {
      if (nodeIndex != null) {
        setPayload(form.watch(`nodes.${nodeIndex}.config.payload`))
      } else {
        setPayload(initialPayload)
      }
    }
  }, [form, isOpen, nodeIndex, initialPayload])

  const isDirty =
    nodeIndex != null
      ? form.getValues(`nodes.${nodeIndex}.config.payload`) !== payload
      : initialPayload !== payload

  const lastValidPayloadRef = useRef(renderedPayload)
  if (isValidPayload) lastValidPayloadRef.current = renderedPayload

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="w-fit">
          <PencilIcon className="size-4" />
          {t("campaigns.action.availableActions.SendWebhook.config.editPayload")}
        </Button>
      </DialogTrigger>
      <DialogContent className="flex max-h-[90dvh] min-h-0 w-full flex-col overflow-hidden sm:max-w-4xl sm:w-[calc(100vw-2rem)]">
        <DialogHeader className="shrink-0">
          <DialogTitle>
            {t("campaigns.action.availableActions.SendWebhook.config.editPayload")}
          </DialogTitle>
          <DialogDescription className="flex flex-col gap-2 items-start">
            <span>
              <Trans
                i18nKey="campaigns.action.availableActions.SendWebhook.config.editPayloadDescription"
                components={{
                  b: <span className="font-medium" />,
                  a: (
                    <a
                      href="https://shopify.dev/docs/api/liquid"
                      target="_blank"
                      rel="noreferrer"
                      className="underline hover:opacity-80"
                    />
                  ),
                }}
              />
            </span>
          </DialogDescription>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden md:flex-row">
          <div className="flex min-h-0 flex-1 min-w-0 flex-col gap-4 overflow-hidden">
            <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden px-1.5">
              <Label className="shrink-0">
                {t("campaigns.action.availableActions.SendWebhook.config.template")}
              </Label>
              <VaultVariableAutocomplete variables={[]} className="flex min-h-0 flex-1">
                <textarea
                  className="min-h-0 w-full flex-1 resize-none overflow-y-auto rounded-md border border-input bg-background px-3 py-2 font-mono text-sm text-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  style={{ tabSize: 2 }}
                  value={payload}
                  placeholder={t(
                    "campaigns.action.availableActions.SendWebhook.config.payloadPlaceholder",
                  )}
                  onKeyDown={(e) => {
                    // Allow Enter key for line breaks, prevent form submission
                    if (e.key === "Enter") {
                      e.stopPropagation()
                    }
                  }}
                  onChange={(e) => {
                    setPayload(e.target.value)
                  }}
                />
              </VaultVariableAutocomplete>
              <Button
                variant="outline"
                size="sm"
                className="w-fit shrink-0"
                onClick={() => setPayload(CAMPAIGN_ACTION_WEBHOOK_DEFAULT_PAYLOAD)}
              >
                <Braces className="size-4" />
                {t("campaigns.action.availableActions.SendWebhook.config.useDefaultPayload")}
              </Button>
            </div>
            <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
              <Label className="flex shrink-0 items-center gap-1">
                <span>{t("campaigns.action.availableActions.SendWebhook.config.data")}</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <IconInfoCircle className="size-4" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <Trans
                      i18nKey="campaigns.action.availableActions.SendWebhook.config.dataTooltip"
                      components={{
                        b: <span className="font-medium" />,
                      }}
                      values={{ eventName }}
                    />
                  </TooltipContent>
                </Tooltip>
              </Label>
              <MarkdownRenderer
                className="flex min-h-0 flex-1 flex-col overflow-auto rounded-md border border-border"
                cardClassName="border-none shadow-none"
                codeClassName="border-none"
              >
                {`\`\`\`json\n${JSON.stringify(eventPayload, null, 2)}\n\`\`\``}
              </MarkdownRenderer>
            </div>
          </div>
          <div className="flex min-h-0 flex-1 min-w-0 flex-col gap-2">
            <Label className="shrink-0">
              {t("campaigns.action.availableActions.SendWebhook.config.preview")}
            </Label>
            <MarkdownRenderer
              className="flex min-h-0 flex-1 flex-col overflow-auto rounded-md border border-border"
              cardClassName="border-none shadow-none"
              codeClassName="border-none"
            >
              {`\`\`\`json
${
  lastValidPayloadRef.current
    ? JSON.stringify(JSON.parse(lastValidPayloadRef.current), null, 2)
    : t("campaigns.action.availableActions.SendWebhook.config.invalidTemplate")
}
\`\`\``}
            </MarkdownRenderer>
          </div>
        </div>
        <DialogFooter className="shrink-0">
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            {t("shared.cancel")}
          </Button>
          <Button
            onClick={() => {
              onChangePayload(payload)
              setIsOpen(false)
            }}
            disabled={!isValidPayload || !isDirty}
          >
            {t("shared.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
